export const RERUN_VIEWER_VERSION = "0.31.4";

export type SessionStatus = "UPLOADING" | "VERIFYING" | "READY" | "FAILED" | "DELETING";

export interface ManifestFile {
  role: string;
  path: string;
  size_bytes: number;
  sha256: string;
  content_type: string;
}

export interface SessionManifest {
  schema_version: string;
  session_id: string;
  name: string;
  trigger_source: string;
  started_at_ns: string;
  stopped_at_ns: string;
  duration_s: number;
  outcome: string;
  recording_schema: unknown;
  rrd_available?: boolean;
  taxonomy?: { id: string; sha256: string };
  stats?: { event_count: number; controller_count: number; [key: string]: unknown };
  files: ManifestFile[];
  metadata?: Record<string, unknown>;
  timelines?: Array<{ name: string; clock_domain: string; min_ns: string; max_ns: string; sample_count: number }>;
  devices?: Array<{ device_id: string; kind: string; entity_path: string }>;
  [key: string]: unknown;
}

export interface SessionRecord {
  session_id: string;
  name: string;
  trigger_source: string;
  started_at_ns: string;
  duration_s: number;
  outcome: string;
  rrd_size_bytes: number;
  rrd_available: boolean;
  automatic_label_count: number;
  status: SessionStatus;
  error: string;
  created_at: string;
  updated_at: string;
  manifest: SessionManifest;
}

export interface LocalUploadFile { path: string; url: string }
export interface LocalUploadInstructions { strategy: "local_put"; files: LocalUploadFile[] }
export interface OssUploadFile { path: string; upload_id: string }
export interface OssUploadInstructions {
  strategy: "oss_multipart";
  part_size_bytes: number;
  files: OssUploadFile[];
  source_marker: LocalUploadFile;
}
export type UploadInstructions = LocalUploadInstructions | OssUploadInstructions;
export interface CreateSessionResponse { session: SessionRecord; upload: UploadInstructions }
export interface PlaybackResponse {
  rrd_url: string;
  viewer_url: string;
  rerun_version: string;
  viewer_version: string;
  mobile?: { rrd_url: string | null; optimized: boolean; profile: "single_3d_lod_v3" | "single_3d_lod_v2" | "single_3d_no_images_v1" | "original" | "unavailable" };
  delivery?: { mode: "cdn_signed_range" | "oss_signed_range" | "application_signed_range"; range_supported: boolean; expires_in_s: number };
  verification?: { method: "sha256"; sha256: string; size_bytes: number; server_verified: boolean };
}
export interface AutomaticLabel {
  id: string;
  session_id: string;
  label_key: string;
  display_name: string;
  category: string;
  scope: "point" | "range" | "sample" | "episode";
  start_time_ns: string;
  end_time_ns: string;
  timeline: "sensor_time" | "system_time";
  source_type: string;
  source_id: string;
  attributes: Record<string, unknown>;
  event_ids: string[];
  immutable: true;
}
export interface AuthUser { id?: string; username: string; display_name?: string }
export interface TokenResponse {
  access_token: string;
  expires_in?: number;
  token_type?: string;
  user?: AuthUser;
}

export type PageSize = 20 | 50;
export interface PageRequest {
  page: number;
  page_size: PageSize;
  query?: string;
  status?: string;
  sort?: string;
}
export interface PageResult<T> {
  items: T[];
  total: number;
  page: number;
  page_size: PageSize;
  pages: number;
  summary?: Record<string, number>;
}

export type OrganizationRole = "owner" | "maintainer" | "supervisor" | "worker";
export interface Organization { id: string; slug: string; name: string; role: OrganizationRole; created_at: string }
export interface Member { organization_id: string; user_id: string; username: string; display_name: string; role: OrganizationRole }
export interface Invitation { id: string; organization_id: string; inviter_id: string; invitee_username: string; invitee_display_name: string; role: OrganizationRole; status: "pending" | "accepted" | "revoked" | "expired"; expires_at: string; created_at: string; accepted_at?: string | null; invite_url?: string }
export type AnnotationKind = "point" | "range";
export interface LabelDefinition {
  name: string;
  description?: string;
  color?: string;
  kind?: AnnotationKind;
  attributes?: Record<string, { type: "boolean" | "select"; values?: unknown[] }>;
}
export interface LabelSchema { labels: LabelDefinition[] }
export interface Project { id: string; organization_id: string; name: string; description: string; label_schema: LabelSchema; schema_version: number }
export interface Task { id: string; project_id: string; session_id: string; name: string; status: string; created_at: string; project_name?: string }
export interface Job { id: string; task_id: string; project_id: string; session_id: string; assignee_id: string | null; timeline: string; stage: string; state: string; start_time_ns: string; end_time_ns: string; version: number; project_name?: string; task_name?: string; effective_role?: OrganizationRole; allowed_actions?: { edit_annotations: boolean; submit: boolean; review: boolean } }
export interface Annotation { id: string; job_id: string; start_time_ns: string; end_time_ns: string; label: string; attributes: Record<string, unknown>; notes: string; schema_version: number; version: number }
export interface Review { id: string; job_id: string; reviewer_id: string; verdict: string; comment: string; created_at: string }
export interface Issue { id: string; job_id: string; author_id: string; body: string; status: string; created_at: string }
